import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const AuthContext = createContext();

function normalizeUser(userData) {
  if (!userData) return null;
  const directPermissions = Array.isArray(userData.permissions) ? userData.permissions : [];
  const effectivePermissions = Array.isArray(userData.effective_permissions)
    ? userData.effective_permissions
    : directPermissions;

  return {
    ...userData,
    permissions: directPermissions,
    effective_permissions: effectivePermissions,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored session on mount
    const storedUser = localStorage.getItem('fiscalpos_user');
    const storedToken = localStorage.getItem('fiscalpos_token');
    
    if (storedUser && storedToken) {
      try {
        setUser(normalizeUser(JSON.parse(storedUser)));
        setToken(storedToken);
      } catch (e) {
        localStorage.removeItem('fiscalpos_user');
        localStorage.removeItem('fiscalpos_token');
      }
    }
    setIsLoading(false);
  }, []);

  const login = useCallback((userData, authToken) => {
    const normalizedUser = normalizeUser(userData);
    setUser(normalizedUser);
    setToken(authToken);
    localStorage.setItem('fiscalpos_user', JSON.stringify(normalizedUser));
    if (authToken) {
      localStorage.setItem('fiscalpos_token', authToken);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('fiscalpos_user');
    localStorage.removeItem('fiscalpos_token');
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const updated = normalizeUser({ ...prev, ...updates });
      localStorage.setItem('fiscalpos_user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'manager' || user?.role === 'admin',
    isWaiter: user?.role === 'waiter',
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
